import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listConnectors } from '../api/connectors.js';
import {
  createWaFlow,
  deleteWaFlow,
  deprecateWaFlow,
  getWaFlowJson,
  listWaFlows,
  publishWaFlow,
  updateWaFlowJson,
  type WaFlow,
  type WaFlowStatus,
} from '../api/waFlows.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';

/**
 * #163 — UI WhatsApp Flows (Meta interactive forms).
 *
 * Flujo:
 * 1. Elegir un connector meta_native del tenant.
 * 2. Listar flows del provider.
 * 3. Crear (con flow_json opcional), publicar, deprecar, eliminar.
 * 4. Editar JSON inline (textarea — slice 1, validador rico viene después).
 *
 * Decisión: no persistimos flows localmente. La fuente de verdad es Meta —
 * leemos siempre del provider. Esto simplifica consistencia a cambio de
 * un round-trip extra. Para envío vía template/campaign solo necesitamos
 * el flow_id (ya devuelto por list()).
 */

const STATUS_COLOR: Record<WaFlowStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  PUBLISHED: 'bg-green-100 text-green-800',
  DEPRECATED: 'bg-amber-100 text-amber-800',
  BLOCKED: 'bg-red-100 text-red-800',
  THROTTLED: 'bg-orange-100 text-orange-800',
};

const FLOW_CATEGORIES = [
  'SIGN_UP',
  'SIGN_IN',
  'APPOINTMENT_BOOKING',
  'LEAD_GENERATION',
  'CONTACT_US',
  'CUSTOMER_SUPPORT',
  'SURVEY',
  'OTHER',
];

export function WhatsAppFlowsList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);
  const [connectorId, setConnectorId] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<WaFlow | null>(null);

  const connectorsQuery = useQuery({
    queryKey: ['connectors', id],
    queryFn: () => listConnectors(id),
    enabled: Boolean(id),
  });
  const metaConnectors = useMemo(
    () => (connectorsQuery.data ?? []).filter((c) => c.provider === 'meta_native'),
    [connectorsQuery.data],
  );

  const flowsQuery = useQuery({
    queryKey: ['wa-flows', id, connectorId],
    queryFn: () => listWaFlows(id, connectorId),
    enabled: Boolean(id && connectorId),
  });

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ['wa-flows', id, connectorId] });
  };

  const publishMut = useMutation({
    mutationFn: (flowId: string) => publishWaFlow(id, connectorId, flowId),
    onSuccess: () => { pushToast('success', 'Flow publicado'); invalidate(); },
    onError: (e: unknown) => pushToast('error', (e as ApiError).message),
  });
  const deprecateMut = useMutation({
    mutationFn: (flowId: string) => deprecateWaFlow(id, connectorId, flowId),
    onSuccess: () => { pushToast('success', 'Flow deprecado'); invalidate(); },
    onError: (e: unknown) => pushToast('error', (e as ApiError).message),
  });
  const deleteMut = useMutation({
    mutationFn: (flowId: string) => deleteWaFlow(id, connectorId, flowId),
    onSuccess: () => { pushToast('success', 'Flow eliminado'); invalidate(); },
    onError: (e: unknown) => pushToast('error', (e as ApiError).message),
  });

  const flows = flowsQuery.data ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">WhatsApp Flows</h2>
          <p className="text-sm text-slate-500">
            Formularios interactivos de Meta (sign-up, citas, encuestas).
          </p>
        </div>
        <Link
          to={`/tenants/${id}`}
          className="text-sm text-brand-600 hover:underline"
        >
          ← Tenant
        </Link>
      </div>

      <div className="mb-4 rounded border border-slate-200 bg-white p-4">
        <label className="text-sm">
          <span className="mb-1 block font-medium text-slate-700">Connector Meta</span>
          <select
            value={connectorId}
            onChange={(e) => setConnectorId(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm md:w-96"
          >
            <option value="">— Elegir un connector meta_native —</option>
            {metaConnectors.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.wabaId ? `(WABA ${c.wabaId})` : '(sin WABA)'}
              </option>
            ))}
          </select>
        </label>
        {metaConnectors.length === 0 && (
          <p className="mt-2 text-xs text-amber-700">
            No hay connectors meta_native configurados. Creá uno en Conexiones.
          </p>
        )}
      </div>

      {connectorId && (
        <>
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {flows.length} flow{flows.length === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
            >
              Nuevo flow
            </button>
          </div>

          {flowsQuery.isLoading && <p className="text-slate-500">Consultando Meta…</p>}
          {flowsQuery.error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {(flowsQuery.error as Error).message}
            </div>
          )}

          {flows.length > 0 && (
            <div className="overflow-x-auto rounded border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Nombre</th>
                    <th className="px-4 py-2">ID</th>
                    <th className="px-4 py-2">Estado</th>
                    <th className="px-4 py-2">Categorías</th>
                    <th className="px-4 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {flows.map((f) => (
                    <tr key={f.id}>
                      <td className="px-4 py-2 font-medium">{f.name}</td>
                      <td className="px-4 py-2 font-mono text-xs">{f.id}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded px-2 py-0.5 text-xs ${STATUS_COLOR[f.status]}`}>
                          {f.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs">{f.categories.join(', ')}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setEditing(f)}
                            className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                          >
                            Editar JSON
                          </button>
                          {f.status === 'DRAFT' && (
                            <button
                              type="button"
                              onClick={() => publishMut.mutate(f.id)}
                              disabled={publishMut.isPending}
                              className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700 disabled:opacity-50"
                            >
                              Publicar
                            </button>
                          )}
                          {f.status === 'PUBLISHED' && (
                            <button
                              type="button"
                              onClick={() => deprecateMut.mutate(f.id)}
                              disabled={deprecateMut.isPending}
                              className="rounded bg-amber-600 px-2 py-1 text-xs text-white hover:bg-amber-700 disabled:opacity-50"
                            >
                              Deprecar
                            </button>
                          )}
                          {f.status === 'DRAFT' && (
                            <button
                              type="button"
                              onClick={() => {
                                if (confirm(`¿Eliminar flow "${f.name}"? Solo posible en DRAFT.`)) {
                                  deleteMut.mutate(f.id);
                                }
                              }}
                              disabled={deleteMut.isPending}
                              className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {creating && connectorId && (
        <FlowCreateModal
          tenantId={id}
          connectorId={connectorId}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); invalidate(); }}
        />
      )}

      {editing && connectorId && (
        <FlowJsonEditor
          tenantId={id}
          connectorId={connectorId}
          flow={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate(); }}
        />
      )}
    </div>
  );
}

function FlowCreateModal({
  tenantId, connectorId, onClose, onCreated,
}: {
  tenantId: string;
  connectorId: string;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const pushToast = useUiStore((s) => s.pushToast);
  const [name, setName] = useState('');
  const [categories, setCategories] = useState<string[]>(['OTHER']);
  const [flowJson, setFlowJson] = useState('');

  const mut = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Nombre requerido');
      if (categories.length === 0) throw new Error('Al menos una categoría');
      let body: { name: string; categories: string[]; flowJson?: string } = { name, categories };
      if (flowJson.trim()) {
        try { JSON.parse(flowJson); } catch { throw new Error('flow_json no es JSON válido'); }
        body = { ...body, flowJson };
      }
      return createWaFlow(tenantId, connectorId, body);
    },
    onSuccess: () => { pushToast('success', 'Flow creado'); onCreated(); },
    onError: (e: unknown) => pushToast('error', (e as Error).message),
  });

  return (
    <Modal title="Nuevo WhatsApp Flow" onClose={onClose}>
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Nombre</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            placeholder="Survey post-compra"
          />
        </label>
        <fieldset>
          <legend className="mb-1 text-sm font-medium text-slate-700">Categorías</legend>
          <div className="flex flex-wrap gap-2 text-xs">
            {FLOW_CATEGORIES.map((c) => (
              <label key={c} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={categories.includes(c)}
                  onChange={(e) => {
                    setCategories((prev) =>
                      e.target.checked ? [...prev, c] : prev.filter((x) => x !== c),
                    );
                  }}
                />
                <span className="font-mono">{c}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">
            flow_json (opcional, podés cargarlo luego en Editar)
          </span>
          <textarea
            value={flowJson}
            onChange={(e) => setFlowJson(e.target.value)}
            rows={8}
            placeholder='{"version":"3.0","screens":[...]}'
            className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
          />
        </label>
      </div>
      <ModalFooter
        onClose={onClose}
        onConfirm={() => mut.mutate()}
        confirmLabel={mut.isPending ? 'Creando…' : 'Crear'}
        disabled={mut.isPending}
      />
    </Modal>
  );
}

function FlowJsonEditor({
  tenantId, connectorId, flow, onClose, onSaved,
}: {
  tenantId: string;
  connectorId: string;
  flow: WaFlow;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const pushToast = useUiStore((s) => s.pushToast);
  const [text, setText] = useState('');
  const [loaded, setLoaded] = useState(false);

  const loadQuery = useQuery({
    queryKey: ['wa-flow-json', tenantId, connectorId, flow.id],
    queryFn: () => getWaFlowJson(tenantId, connectorId, flow.id),
    enabled: !loaded,
  });

  if (loadQuery.data && !loaded) {
    setText(loadQuery.data);
    setLoaded(true);
  }

  const mut = useMutation({
    mutationFn: async () => {
      try { JSON.parse(text); } catch { throw new Error('flow_json no es JSON válido'); }
      await updateWaFlowJson(tenantId, connectorId, flow.id, text);
    },
    onSuccess: () => { pushToast('success', 'flow_json actualizado'); onSaved(); },
    onError: (e: unknown) => pushToast('error', (e as Error).message),
  });

  return (
    <Modal title={`Editar flow_json — ${flow.name}`} onClose={onClose}>
      {loadQuery.isLoading && <p className="text-slate-500">Cargando JSON desde Meta…</p>}
      {loadQuery.error && (
        <p className="text-red-700">{(loadQuery.error as Error).message}</p>
      )}
      {loaded && (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={20}
          className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-[11px]"
        />
      )}
      <ModalFooter
        onClose={onClose}
        onConfirm={() => mut.mutate()}
        confirmLabel={mut.isPending ? 'Guardando…' : 'Guardar'}
        disabled={mut.isPending || !loaded}
      />
    </Modal>
  );
}

function Modal({
  title, onClose, children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-3">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({
  onClose, onConfirm, confirmLabel, disabled,
}: {
  onClose: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
      <button
        type="button"
        onClick={onClose}
        className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={disabled}
        className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
      >
        {confirmLabel}
      </button>
    </div>
  );
}
